import { forwardRef, useImperativeHandle, useState, useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2 } from 'lucide-react'
import type { z } from 'zod'

import { agentConfigSchema } from '@/schemas/workflow-node'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import api from '@/services/api'
import { useOrganizationStore } from '@/stores/organization-store'

type AgentConfig = z.infer<typeof agentConfigSchema>

export interface NodeFormProps {
  defaultValues: Record<string, unknown>
  onSubmit: (data: Record<string, unknown>) => void
  disabled?: boolean
}

export interface NodeFormHandle {
  submit: () => void
}

interface AgentOption {
  id: string
  name: string
}

export const AgentNodeForm = forwardRef<NodeFormHandle, NodeFormProps>(
  ({ defaultValues, onSubmit, disabled }, ref) => {
    const { t } = useTranslation('workflows')
    const [agents, setAgents] = useState<AgentOption[]>([])
    const [loadingAgents, setLoadingAgents] = useState(false)
    const currentProject = useOrganizationStore((s) => s.currentProject)

    const {
      register,
      handleSubmit,
      control,
      setValue,
      watch,
      formState: { errors },
    } = useForm<AgentConfig>({
      resolver: zodResolver(agentConfigSchema),
      defaultValues: defaultValues as AgentConfig,
    })

    const { fields, append, remove } = useFieldArray({
      control,
      name: 'input_overrides',
    })

    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit((data) => onSubmit(data as Record<string, unknown>))()
      },
    }))

    const agentId = watch('agent_id')

    // Fetch agents scoped to current project (T-02-05 mitigation)
    useEffect(() => {
      if (!currentProject?.id) return

      let cancelled = false
      setLoadingAgents(true)

      api
        .get<{ items: AgentOption[] }>('/agents', {
          params: { project_id: currentProject.id },
        })
        .then((res) => {
          if (!cancelled) {
            setAgents(res.data.items ?? [])
          }
        })
        .catch(() => {
          if (!cancelled) {
            setAgents([])
          }
        })
        .finally(() => {
          if (!cancelled) setLoadingAgents(false)
        })

      return () => {
        cancelled = true
      }
    }, [currentProject?.id])

    return (
      <form
        className="space-y-4"
        onSubmit={handleSubmit((data) => onSubmit(data as Record<string, unknown>))}
      >
        {/* Agent ID */}
        <div className="space-y-2">
          <Label htmlFor="agent_id">Agent</Label>
          {agents.length > 0 || loadingAgents ? (
            <Select
              value={agentId || undefined}
              onValueChange={(value) => setValue('agent_id', value)}
              disabled={disabled || loadingAgents}
            >
              <SelectTrigger className="bg-white/5 border-gray-700/50">
                <SelectValue placeholder={loadingAgents ? 'Loading...' : 'Select agent'} />
              </SelectTrigger>
              <SelectContent>
                {agents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className="text-sm text-muted-foreground py-2">
              {t('flow.helperAgentEmpty')}
            </p>
          )}
          {errors.agent_id && (
            <p className="text-sm text-destructive">{t('flow.requiredField')}</p>
          )}
        </div>

        {/* Input Overrides */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Input Overrides</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1 border-gray-700/50 text-gray-300"
              onClick={() => append({ key: '', value: '' })}
              disabled={disabled}
            >
              <Plus className="w-4 h-4" />
              Add override
            </Button>
          </div>
          {fields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2">
              <Input
                className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
                placeholder="Key"
                disabled={disabled}
                {...register(`input_overrides.${index}.key`)}
              />
              <Input
                className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
                placeholder="Value"
                disabled={disabled}
                {...register(`input_overrides.${index}.value`)}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0 text-gray-500 hover:text-red-400 shrink-0"
                onClick={() => remove(index)}
                disabled={disabled}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </form>
    )
  }
)

AgentNodeForm.displayName = 'AgentNodeForm'
